import React, { useState, useEffect, useRef, useMemo } from "react";
import SectionHeader from "../../../components/sectionHeader/sectionHeader";
import Button from "../../../components/button/button";
import InputDropdown from "../../../components/inputs/inputDropdown/inputDropdown";
import InputSearch from "../../../components/inputs/inputSearch/inputSearch";
import Table from "../../../components/table/table";
import Modal from "../../../components/modal/modal";
import Notification from "../../../components/notification/notification";
import Accordion from "../../../components/accordion/accordion";
import Tag from "../../../components/tag/tag";
import { BiImport, BiExport } from "react-icons/bi";
import { FiFilter } from "react-icons/fi";
import { getApiInstance } from "../../../utils/api";
import { searchByKeywords } from "../../../utils/searchUtils";
import "./presupuestosMC.scss";

const PresupuestosMC = () => {
    const api = getApiInstance();
    const notificationRef = useRef();
    
    // Estados principales
    const [importaciones, setImportaciones] = useState([]);
    const [selectedImportacion, setSelectedImportacion] = useState(null);
    const [tarifasData, setTarifasData] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Estados de exportación
    const [isExporting, setIsExporting] = useState(false);
    
    // Estados de selección de filas
    const [selectedRows, setSelectedRows] = useState([]);
    
    // Estados del modal de importación
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [tipoImportacion, setTipoImportacion] = useState(""); // "audiovisuales" o "iluminacion"
    
    // Estados de búsqueda y filtros
    const [searchTerm, setSearchTerm] = useState("");
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [filterCategoria, setFilterCategoria] = useState([]);

    // Cargar lista de importaciones al montar el componente
    useEffect(() => {
        fetchImportaciones();
    }, []);

    // Limpiar selección cuando cambian las tarifas filtradas
    useEffect(() => {
        setSelectedRows([]);
    }, [searchTerm, filterCategoria]);

    // Cargar las importaciones disponibles
    const fetchImportaciones = async () => {
        try {
            setLoading(true);
            const response = await api.get("/api/TarifasMc/importaciones");
            
            if (response.status === 200) {
                const importacionesData = response.data;
                setImportaciones(importacionesData);
                
                // Seleccionar automáticamente la más reciente si existe
                if (importacionesData.length > 0 && !selectedImportacion) {
                    const masReciente = importacionesData[0]; // Asumiendo que vienen ordenadas por fecha desc
                    setSelectedImportacion(masReciente);
                    fetchTarifasDeImportacion(masReciente.codImportacionMc || masReciente.CodImportacionMc);
                }
            }
        } catch (error) {
            console.error("Error al cargar importaciones:", error);
            notificationRef.current?.addNotification(
                "Error al cargar el historial de importaciones",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    // Cargar tarifas de una importación específica
    const fetchTarifasDeImportacion = async (codImportacion) => {
        try {
            setLoading(true);
            const response = await api.get(`/api/TarifasMc/importaciones/${codImportacion}`);
            
            if (response.status === 200) {
                setTarifasData(response.data.tarifas || []);
            }
        } catch (error) {
            console.error("Error al cargar tarifas de la importación:", error);
            notificationRef.current?.addNotification(
                "Error al cargar los datos de la importación seleccionada",
                "error"
            );
            setTarifasData([]);
        } finally {
            setLoading(false);
        }
    };

    // Manejar selección de importación en el dropdown
    const handleImportacionSelect = (option) => {
        setSelectedImportacion(option);
        setSelectedRows([]); // Limpiar filas seleccionadas al cambiar importación
        
        if (option && (option.codImportacionMc || option.CodImportacionMc)) {
            fetchTarifasDeImportacion(option.codImportacionMc || option.CodImportacionMc);
        } else {
            setTarifasData([]);
        }
    };

    // Abrir modal de importación con tipo específico
    const openImportModal = (tipo) => {
        setTipoImportacion(tipo);
        setIsImportModalOpen(true);
        setImportFile(null);
    };

    // Cerrar modal de importación
    const closeImportModal = () => {
        setIsImportModalOpen(false);
        setImportFile(null);
        setIsImporting(false);
        setTipoImportacion("");
    };

    // Manejar selección de archivo
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validar que sea un archivo Excel
            const validExtensions = ['.xlsx', '.xls'];
            const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
            
            if (validExtensions.includes(fileExtension)) {
                setImportFile(file);
            } else {
                notificationRef.current?.addNotification(
                    "Por favor selecciona un archivo Excel válido (.xlsx, .xls)",
                    "warning"
                );
                event.target.value = '';
            }
        }
    };

    // Ejecutar importación específica
    const handleImport = async () => {
        if (!importFile) {
            notificationRef.current?.addNotification(
                "Por favor selecciona un archivo para importar",
                "warning"
            );
            return;
        }

        if (!tipoImportacion) {
            notificationRef.current?.addNotification(
                "Error: Tipo de importación no especificado",
                "error"
            );
            return;
        }

        try {
            setIsImporting(true);
            
            const formData = new FormData();
            formData.append('file', importFile);

            // Seleccionar endpoint según el tipo de importación
            const endpoint = tipoImportacion === "audiovisuales" 
                ? "/api/TarifasMc/importar-audiovisuales"
                : "/api/TarifasMc/importar-iluminacion";

            const response = await api.post(endpoint, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.status === 200) {
                const tipoTexto = tipoImportacion === "audiovisuales" ? "audiovisuales" : "iluminación";
                notificationRef.current?.addNotification(
                    `Importación de ${tipoTexto} completada exitosamente. ${response.data.totalTarifasInsertadas || response.data.registrosImportados || 0} registros procesados.`,
                    "success"
                );
                
                // Recargar importaciones y seleccionar la nueva
                await fetchImportaciones();
                closeImportModal();
            }
        } catch (error) {
            console.error("Error durante la importación:", error);
            let errorMsg = "Error al importar el archivo";
            
            if (error.response?.data?.detail) {
                errorMsg = error.response.data.detail;
            } else if (error.response?.data?.message) {
                errorMsg = error.response.data.message;
            }
            
            notificationRef.current?.addNotification(errorMsg, "error");
        } finally {
            setIsImporting(false);
        }
    };

    // Configuración de las columnas de la tabla
    const tableColumns = [
        {
            key: "codigoArticulo",
            label: "Código Artículo",
            width: "150px",
            render: (row) => row.codigoArticulo || row.CodigoArticulo || ""
        },
        {
            key: "descripcion", 
            label: "Descripción",
            width: "300px",
            render: (row) => row.descripcion || row.Descripcion || ""
        },
        {
            key: "precio",
            label: "Precio",
            width: "120px",
            render: (row) => {
                const precio = row.precio || row.Precio || 0;
                return Number(precio).toLocaleString('es-ES', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                });
            }
        },
        {
            key: "divisa",
            label: "Divisa",
            width: "80px",
            render: (row) => row.divisa || row.Divisa || "€"
        },
        {
            key: "unidad",
            label: "Unidad",
            width: "100px",
            render: (row) => row.unidad || row.Unidad || ""
        },
        {
            key: "nombreFamiliaRevit",
            label: "Nombre Familia Revit",
            width: "200px",
            render: (row) => row.nombreFamiliaRevit || row.NombreFamiliaRevit || "-"
        },
        {
            key: "nombreTipoRevit",
            label: "Nombre Tipo Revit",
            width: "200px",
            render: (row) => row.nombreTipoRevit || row.NombreTipoRevit || "-"
        },
        {
            key: "codigoElementoAsociado1",
            label: "Código Tarifa Elemento Asociado 1",
            width: "220px",
            render: (row) => row.codigoArticuloComponente1 || row.CodigoArticuloComponente1 || "-"
        },
        {
            key: "udElementoAsociado1",
            label: "UD Elemento Asociado 1",
            width: "150px",
            render: (row) => row.unidadesComponente1 || row.UnidadesComponente1 || "-"
        },
        {
            key: "codigoElementoAsociado2",
            label: "Código Tarifa Elemento Asociado 2",
            width: "220px",
            render: (row) => row.codigoArticuloComponente2 || row.CodigoArticuloComponente2 || "-"
        },
        {
            key: "udElementoAsociado2",
            label: "UD Elemento Asociado 2",
            width: "150px",
            render: (row) => row.unidadesComponente2 || row.UnidadesComponente2 || "-"
        },
        {
            key: "codigoElementoAsociado3",
            label: "Código Tarifa Elemento Asociado 3",
            width: "220px",
            render: (row) => row.codigoArticuloComponente3 || row.CodigoArticuloComponente3 || "-"
        },
        {
            key: "udElementoAsociado3",
            label: "UD Elemento Asociado 3",
            width: "150px",
            render: (row) => row.unidadesComponente3 || row.UnidadesComponente3 || "-"
        },
        {
            key: "codigoElementoAsociado4",
            label: "Código Tarifa Elemento Asociado 4",
            width: "220px",
            render: (row) => row.codigoArticuloComponente4 || row.CodigoArticuloComponente4 || "-"
        },
        {
            key: "udElementoAsociado4",
            label: "UD Elemento Asociado 4",
            width: "150px",
            render: (row) => row.unidadesComponente4 || row.UnidadesComponente4 || "-"
        },
        {
            key: "nombreFichaTecnica",
            label: "Nombre Ficha Técnica",
            width: "200px",
            render: (row) => row.nombreFichaTecnica || row.NombreFichaTecnica || "-"
        },
        {
            key: "nombreManualMantenimiento",
            label: "Nombre Manual Uso/Mantenimiento",
            width: "220px",
            render: (row) => row.nombreManualMantenimiento || row.NombreManualMantenimiento || "-"
        },
        {
            key: "garantiaMeses",
            label: "Garantía (meses)",
            width: "120px",
            render: (row) => row.garantiaMeses || row.GarantiaMeses || "-"
        },
        {
            key: "fechaImportacion",
            label: "Fecha Importación",
            width: "140px",
            render: (row) => {
                const fecha = row.fechaImportacion || row.FechaImportacion;
                return fecha ? new Date(fecha).toLocaleDateString('es-ES') : "-";
            }
        },
        {
            key: "activo",
            label: "Estado",
            width: "100px",
            render: (row) => {
                const activo = row.activo !== undefined ? row.activo : row.Activo;
                return activo ? "Activo" : "Inactivo";
            }
        }
    ];

    // Filtrar y buscar tarifas
    const filteredTarifasData = useMemo(() => {
        let filtered = tarifasData;

        // Filtrar por término de búsqueda
        if (searchTerm) {
            filtered = searchByKeywords(filtered, searchTerm, [
                'codigoArticulo',
                'descripcion',
                'tipoTarifa',
                'nombreTipoRevit',
                'nombreFamiliaRevit',
                'hojaOrigen',
                'nombreFichaTecnica',
                'nombreManualMantenimiento',
                'codigoArticuloComponente1',
                'codigoArticuloComponente2',
                'codigoArticuloComponente3',
                'codigoArticuloComponente4'
            ]);
        }

        // Filtrar por categoría
        if (filterCategoria.length > 0) {
            filtered = filtered.filter(tarifa => 
                filterCategoria.includes(tarifa.tipoTarifa || tarifa.TipoTarifa)
            );
        }

        return filtered;
    }, [tarifasData, searchTerm, filterCategoria]);

    // Obtener categorías únicas para los filtros
    const categoriasUnicas = useMemo(() => {
        const categorias = new Set();
        tarifasData.forEach(item => {
            const categoria = item.tipoTarifa || item.TipoTarifa;
            if (categoria) categorias.add(categoria);
        });
        return Array.from(categorias).sort();
    }, [tarifasData]);

    // Manejar selección de categoría en filtros
    const handleSelectCategoria = (categoria) => {
        setFilterCategoria(prev => {
            if (prev.includes(categoria)) {
                return prev.filter(cat => cat !== categoria);
            } else {
                return [...prev, categoria];
            }
        });
    };

    // Limpiar filtros
    const handleCleanFilter = () => {
        setFilterCategoria([]);
    };

    // Aplicar filtros y cerrar modal
    const applyFiltersAndClose = () => {
        setSelectedRows([]); // Limpiar selección al aplicar filtros
        setIsFiltersModalOpen(false);
    };

    // Obtener tipo de tarifa de la importación seleccionada
    const getTipoTarifaFromImportacion = (importacion) => {
        const nombreArchivo = (importacion?.nombreArchivo || "").toLowerCase();
        if (nombreArchivo.includes("audiovisual")) return "audiovisuales";
        if (nombreArchivo.includes("iluminacion") || nombreArchivo.includes("iluminación")) return "iluminacion";
        return null;
    };

    // Descargar plantilla (sin datos)
const handleDownloadTemplate = async () => {
    const tipoTarifa = selectedImportacion ? getTipoTarifaFromImportacion(selectedImportacion) : null;
    
    try {
        setIsExporting(true);

        const params = new URLSearchParams({
            incluirDatos: 'false',
            soloActivas: 'true'
        });

        if (tipoTarifa) {
            params.append('tipoTarifa', tipoTarifa);
        }

        const response = await api.get(`/api/TarifasMc/exportar?${params.toString()}`, {
            responseType: 'blob'
        });

        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const timestamp = new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/[:-]/g, '')
            .replace('T', '');

        const tipoTexto = tipoTarifa ? `_${tipoTarifa}` : '';
        link.download = `TarifasMC${tipoTexto}_plantilla_${timestamp}.xlsx`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        notificationRef.current?.addNotification(
            "Plantilla descargada exitosamente",
            "success"
        );
    } catch (error) {
        console.error("Error al descargar la plantilla:", error);
        const errorMessage =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            "Error al descargar la plantilla";

        notificationRef.current?.addNotification(errorMessage, "error");
    } finally {
        setIsExporting(false);
    }
};

    // Manejar exportación con datos
    const handleExport = async () => {
        if (!selectedImportacion) {
            notificationRef.current?.addNotification(
                "Por favor selecciona una importación para exportar",
                "warning"
            );
            return;
        }

        const tipoTarifa = getTipoTarifaFromImportacion(selectedImportacion);
        
        try {
            setIsExporting(true);
            
            const params = new URLSearchParams({
                incluirDatos: 'true',
                soloActivas: 'true'
            });
            
            if (tipoTarifa) {
                params.append('tipoTarifa', tipoTarifa);
            }
            
            const response = await api.get(`/api/TarifasMc/exportar?${params.toString()}`, {
                responseType: 'blob'
            });
            
            // Crear y descargar el archivo
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '');
            const tipoTexto = tipoTarifa ? `_${tipoTarifa}` : '';
            link.download = `TarifasMC${tipoTexto}_datos_${timestamp}.xlsx`;
            
            link.click();
            window.URL.revokeObjectURL(url);
            
            notificationRef.current?.addNotification(
                "Exportación completada exitosamente",
                "success"
            );
        } catch (error) {
            console.error("Error al exportar:", error);
            const errorMessage = error.response?.data?.detail || error.response?.data?.message || "Error al exportar los datos";
            notificationRef.current?.addNotification(
                errorMessage,
                "error"
            );
        } finally {
            setIsExporting(false);
        }
    };

    // Obtener identificador único de una fila
    const getRowId = (row, index) => {
        return row.codTarifaMc || row.CodTarifaMc || row.id || row.Id || index;
    };

    // Manejar selección de filas
    const handleRowSelect = (selectedRowIds) => {
        setSelectedRows(selectedRowIds);
    };

    // Exportar filas seleccionadas
    const handleExportSelection = async () => {
        if (selectedRows.length === 0) {
            notificationRef.current?.addNotification(
                "Por favor selecciona al menos una fila para exportar",
                "warning"
            );
            return;
        }
    
        const tipoTarifa = selectedImportacion ? getTipoTarifaFromImportacion(selectedImportacion) : null;
        
        try {
            setIsExporting(true);
            
            // Obtener los códigos de tarifa MC de las filas seleccionadas
            const codigosTarifaMc = selectedRows.map(rowId => {
                const row = filteredTarifasData.find((item, index) => getRowId(item, index) === rowId);
                return row ? (row.codTarifaMc || row.CodTarifaMc || rowId) : rowId;
            }).filter(codigo => codigo != null);
    
            if (codigosTarifaMc.length === 0) {
                notificationRef.current?.addNotification(
                    "No se pudieron obtener los identificadores de las filas seleccionadas",
                    "error"
                );
                return;
            }

            const requestBody = {
                CodigosTarifaMc: codigosTarifaMc,
                TipoTarifa: null,
                IncluirDatos: true,
                SoloActivas: true
            };
    
            const response = await api.post('/api/TarifasMc/exportar-seleccion', requestBody, {
                responseType: 'blob'
            });
            
            // Crear y descargar el archivo
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '');
            link.download = `TarifasMC_seleccion_${timestamp}.xlsx`;
            
            link.click();
            window.URL.revokeObjectURL(url);
            
            notificationRef.current?.addNotification(
                `Exportación completada: ${selectedRows.length} filas exportadas exitosamente`,
                "success"
            );
            
            // Limpiar selección después de exportar
            setSelectedRows([]);
            
        } catch (error) {
            console.error("Error al exportar la selección:", error);
            const errorMessage = error.response?.data?.detail || error.response?.data?.message || "Error al exportar la selección";
            notificationRef.current?.addNotification(
                errorMessage,
                "error"
            );
        } finally {
            setIsExporting(false);
        }
    };

    // Preparar opciones para el dropdown de importaciones
    const importacionesOptions = importaciones.map(imp => ({
        label: `${imp.fechaImportacion || imp.FechaImportacion} - ${imp.nombreArchivo || imp.NombreArchivo}`,
        value: imp.codImportacionMc || imp.CodImportacionMc,
        codImportacionMc: imp.codImportacionMc || imp.CodImportacionMc,
        fechaImportacion: imp.fechaImportacion || imp.FechaImportacion,
        nombreArchivo: imp.nombreArchivo || imp.NombreArchivo,
        registrosImportados: imp.totalTarifasInsertadas || imp.TotalTarifasInsertadas || imp.registrosImportados || imp.RegistrosImportados || 0,
        totalFilasLeidas: imp.totalFilasLeidas || imp.TotalFilasLeidas || 0
    }));

    return (
        <div className="presupuestos-mc-container">
            <SectionHeader 
                title="Presupuestos MC"
                subtitle="Gestiona y administra tarifas MC del sistema"
            />
            
            <div className="presupuestos-mc-content">
                {/* Área de controles superior */}
                <div className="presupuestos-mc-controls-area">

                    {/* Fila de búsqueda y filtros */}
                    <div className="controls-row">
                        <div className="search-filter-group">
                            <InputSearch
                                context="tableSearch"
                                placeholder="Buscar..."
                                onSearch={setSearchTerm}
                            />
                            
                            {importaciones.length > 0 && (
                                <div className="presupuestos-mc-selector-inline">
                                    <InputDropdown
                                        label=""
                                        options={importacionesOptions}
                                        onSelect={handleImportacionSelect}
                                        value={selectedImportacion}
                                        placeholder="Selecciona una importación"
                                        hasSearch={true}
                                        showLabel={false}
                                    />
                                </div>
                            )}
                            
                            {tarifasData.length > 0 && (
                                <Button
                                    variant="outline"
                                    label="Filtros"
                                    icon={<FiFilter />}
                                    onClick={() => setIsFiltersModalOpen(true)}
                                />
                            )}
                        </div>
                        
                        <div className="import-export-buttons-group">
                            <Button
                                variant="outline"
                                label={isExporting ? "Descargando..." : "Descargar Plantilla"}
                                icon={<BiExport />}
                                onClick={handleDownloadTemplate}
                                disabled={isExporting}
                            />
                            
                            {selectedImportacion && (
                                <Button
                                    variant="outline"
                                    label={isExporting ? "Exportando..." : "Exportar Datos"}
                                    icon={<BiExport />}
                                    onClick={handleExport}
                                    disabled={isExporting}
                                />
                            )}
                            
                            <Button
                                variant="filled"
                                icon={<BiImport />}
                                label="Importar Audiovisuales"
                                onClick={() => openImportModal("audiovisuales")}
                            />
                            
                            <Button
                                variant="filled"
                                icon={<BiImport />}
                                label="Importar Iluminación"
                                onClick={() => openImportModal("iluminacion")}
                            />
                            
                            {selectedRows.length > 0 && (
                                <Button
                                    variant="secondary"
                                    label={isExporting ? "Exportando..." : `Exportar Selección (${selectedRows.length})`}
                                    icon={<BiExport />}
                                    onClick={handleExportSelection}
                                    disabled={isExporting}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Información de la versión seleccionada */}
                {selectedImportacion && (
                    <div className="importacion-seleccionada">
                        <h3 className="importacion-seleccionada-title">Importación Seleccionada</h3>
                        <div className="importacion-seleccionada-content">
                            <span className="importacion-info">
                                <strong>Archivo:</strong> {selectedImportacion.nombreArchivo}
                            </span>
                            <span className="importacion-info">
                                <strong>Fecha:</strong> {new Date(selectedImportacion.fechaImportacion).toLocaleDateString('es-ES')}
                            </span>
                        </div>
                    </div>
                )}

                {/* Tabla de tarifas */}
                {tarifasData.length > 0 ? (
                    <div className="tarifas-table-container">
                        <Table
                            columns={tableColumns}
                            data={filteredTarifasData}
                            loading={loading}
                            onRowSelect={handleRowSelect}
                            showHeaderCheckbox={true}
                            noResultsMessage={searchTerm || filterCategoria.length > 0 
                                ? "No se han encontrado tarifas que coincidan con los criterios de búsqueda"
                                : "No se han encontrado tarifas en esta importación"
                            }
                        />
                    </div>
                ) : selectedImportacion ? (
                    <div className="no-data-message">
                        <p>No hay datos disponibles para la importación seleccionada</p>
                    </div>
                ) : importaciones.length === 0 && !loading ? (
                    <div className="no-importaciones-message">
                        <h3>No hay importaciones disponibles</h3>
                        <p>Utiliza los botones "Importar Audiovisuales" o "Importar Iluminación" para realizar tu primera importación.</p>
                    </div>
                ) : null}
            </div>

            {/* Modal de importación */}
            <Modal
                isOpen={isImportModalOpen}
                onClose={closeImportModal}
                title={`Importar Tarifas MC - ${tipoImportacion === "audiovisuales" ? "Audiovisuales" : "Iluminación"}`}
                size="M"
                onCancel={closeImportModal}
                onAccept={handleImport}
                outlineButtonLabel="Cancelar"
                primaryButtonLabel={isImporting ? "Importando..." : "Importar"}
            >
                <div className="import-modal-content">
                    <p>Selecciona un archivo Excel con las tarifas MC de {tipoImportacion === "audiovisuales" ? "audiovisuales" : "iluminación"} que deseas importar.</p>
                    
                    <div className="file-input-container">
                        <label htmlFor="import-file" className="file-input-label">
                            <BiImport />
                            {importFile ? importFile.name : "Seleccionar archivo Excel"}
                        </label>
                        <input
                            id="import-file"
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                    </div>

                    {importFile && (
                        <div className="file-info">
                            <p><strong>Archivo seleccionado:</strong> {importFile.name}</p>
                            <p><strong>Tamaño:</strong> {(importFile.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Modal de filtros */}
            <Modal
                title="Filtros"
                isOpen={isFiltersModalOpen}
                onClose={() => setIsFiltersModalOpen(false)}
                onAccept={applyFiltersAndClose}
                outlineButtonLabel="Limpiar"
                primaryButtonLabel={`Ver resultados (${filteredTarifasData.length})`}
                onCancel={handleCleanFilter}
            >
                <div className="modal-inputs">
                    <div className="section-header__modal__section">
                        <Accordion title="Categoría">
                            <ul className="section-header__modal__section__content">
                                {categoriasUnicas.map((categoria) => (
                                    <Tag
                                        key={categoria}
                                        label={categoria}
                                        isSelected={filterCategoria.includes(categoria)}
                                        onSelect={() => handleSelectCategoria(categoria)}
                                    />
                                ))}
                            </ul>
                        </Accordion>
                    </div>
                </div>
            </Modal>

            <Notification ref={notificationRef} />
        </div>
    );
};

export default PresupuestosMC;